import React, { useEffect, useRef, useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  CustomSelect,
  Icon,
  IconName,
  MUIDataTable,
  MUIDataTableColumn,
  Pill,
  TableActionTypes,
  TableState,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { useHistory } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useTheme } from '@mui/material';
import {
  CandidateList,
  RegistrationRequestTab,
} from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import ViewFeeDetailsDialog from './ViewFeeDetailsDialog';
import { ClassesAndTranslation } from '../../../models/ClassesAndTranslation';
import { ComponentUsage } from './model';
import RouteKey from '../../../routes/key';
import { useTabMutation } from './useTabMutation';
import { PaymentStatus } from '../../../models/PaymentStatus';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import { useGetPaymentStatus } from '../../../api-refactored/candidate/useGetPaymentStatus';
import { customFilterChipRender } from '../../../hooks/table';
import {
  PostIndividualPaymentLinkRequest,
  usePostGroupExamRegistrationIndividualPaymentLink,
} from '../../../api-refactored/group-exam-registration/usePostGroupExamRegistrationIndividualPaymentLink';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { GenericErrorResponse } from '../../../api-refactored/typings';

interface ICandidateDataContent extends CandidateList {
  paymentStatus?: PaymentStatus;
}

interface ICandidateDataColumnsForSummary extends ClassesAndTranslation {
  candidateTabData: {
    paymentStatusDropdown: { functionCode: any; typeMapping: { [key: string]: any } };
    dataContent: ICandidateDataContent[];
  };
  componentUsageFor: ComponentUsage;
  dataTableHandler: {
    candidateTabTableState: TableState;
    candidateTabTableDispatch: React.Dispatch<TableActionTypes>;
  };
  apiHooks: {
    postIndividualPaymentLink: UseCustomMutationReturn<
      any,
      AxiosError<GenericErrorResponse>,
      PostIndividualPaymentLinkRequest,
      unknown
    >;
  };
  applicationUuid: string;
  totalFee: string;
}

interface ICandidateDataColumns extends ClassesAndTranslation, ICandidateDataColumnsForSummary {
  setFeeModalOpen: React.Dispatch<
    React.SetStateAction<{ candidateName: string; examRegistrationUuid: string }>
  >;
  history: any;
  totalFee: string;
}

const { exam } = RouteKey.child;

const { examRegistrations, summary } = RouteKey.exam.child;

const candidateDataColumnsForSummary = ({
  candidateTabData,
  t,
  classes,
  theme,
  componentUsageFor,
  dataTableHandler,
  apiHooks,
  applicationUuid,
  totalFee,
}: ICandidateDataColumnsForSummary): MUIDataTableColumn[] => {
  const {
    paymentStatusDropdown: {
      functionCode: filterCodePaymentStatus,
      typeMapping: filterMappingPaymentStatus,
    },
    dataContent,
  } = candidateTabData;
  const { postIndividualPaymentLink } = apiHooks;
  const { candidateTabTableState, candidateTabTableDispatch } = dataTableHandler;
  return [
    {
      name: 'paymentStatus',
      label: t('exam->>RegistrationSummary.PaymentStatus'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        display: componentUsageFor === ComponentUsage.SUMMARY && totalFee !== '0.00',
        customFilterListOptions: {
          render: (v:any) => [filterMappingPaymentStatus[v[0]].children],
          update: ( filterList: string[][],
                    filterPos: number,
                    index: number)  => {
            // update table filter state
            candidateTabTableDispatch({
              type: 'UPDATE_FILTERS',
              payload: { ...candidateTabTableState.filters, candidateType: [] },
            });

            // update table inner filter
            return customFilterChipRender(filterList, filterPos, index);
          },
        },
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              // eslint-disable-next-line react/destructuring-assignment
              defaultValue={filterList[index][0]}
              displayEmpty
              menuItems={filterCodePaymentStatus}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              label={t('exam->>RegistrationSummary.PaymentStatus')}
              labelId="inputSelectLabel-paymentStatus"
              id="inputSelect-paymentStatus"
            />
          ),
        },
        customBodyRenderLite: (dataIndex: number) => {
          const { paymentStatus } = dataContent[dataIndex];
          return (
            <Typography variant="body2">
              <Pill
                variant={paymentStatus === 'Paid' ? 'positive' : 'info'}
                label={paymentStatus || ''}
              />
            </Typography>
          );
        },
      },
    },
    {
      name: '',
      label: t('exam->>RegistrationSummary.PaymentLink'),
      options: {
        sort: false,
        filter: false,
        display: componentUsageFor === ComponentUsage.SUMMARY && totalFee !== '0.00',
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { paymentStatus, examRegistrationUuid, candidateName } = dataContent[dataIndex];
          const { status: mutateStatus, mutate, variables, isLoading } = postIndividualPaymentLink;
          const IsMutatedCandidateName =
            variables?.callbackVariable.candidateName === candidateName;
          const isButtonDisabled =
            paymentStatus === PaymentStatus.PAID || paymentStatus === PaymentStatus.NA || isLoading;
          return (
            <ApiButton
              variant="text"
              color="primary"
              disabled={isButtonDisabled}
              onClick={() =>
                mutate({
                  groupExamRegUuid: applicationUuid,
                  individualExamRegUuid: examRegistrationUuid,
                  callbackVariable: {
                    candidateName,
                  },
                })
              }
              size="small"
              status={IsMutatedCandidateName ? mutateStatus : 'idle'}
              className={classes.sendEmailButton}
              startIcon={
                <Icon
                  name={IconName.email}
                  size="M"
                  color={
                    isButtonDisabled
                      ? theme?.color.link.linkDisabledBg01
                      : theme?.color.link.linkDefault01
                  }
                />
              }
            >
              Send Link
            </ApiButton>
          );
        },
      },
    },
    {
      name: '',
      label: t('exam->>RegistrationSummary.Actions'),
      options: {
        sort: false,
        filter: false,
        display: componentUsageFor === ComponentUsage.SUMMARY,
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Box className={classes.actionButtonMediumLeft}>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                const baseUrl = `${import.meta.env.VITE_APP_HOST_DOMAIN}/${exam}/${examRegistrations.route}`;
                const registrationUuid = dataContent[dataIndex].examRegistrationUuid;
                const individualSummaryRoute = `${baseUrl}/${registrationUuid}/${summary.route}?groupExamRegUuid=${applicationUuid}`;
                window.open(individualSummaryRoute, '_blank');
              }}
            >
              {t('exam->>RegistrationSummary.ViewDetails')}
            </Button>
          </Box>
        ),
      },
    },
  ];
};

const candidateDataColumns = ({
  candidateTabData,
  t,
  classes,
  theme,
  setFeeModalOpen,
  componentUsageFor,
  dataTableHandler,
  apiHooks,
  applicationUuid,
  totalFee,
}: ICandidateDataColumns): MUIDataTableColumn[] => {
  const { dataContent } = candidateTabData;
  return [
    {
      name: 'uinFinPassport',
      label: t('exam->>ReviewExam.NRIC/FIN/Passport No.'),
      options: {
        sort: true,
        filter: false,
        setCellProps: () => ({ style: { width: '15%' } }),
      },
    },
    {
      name: 'candidateName',
      label: t('exam->>ReviewExam.CandidateName'),
      options: {
        sort: true,
        filter: false,
      },
    },
    {
      name: 'examLevel',
      label: t('exam->>ReviewExam.Exam Level'),
      options: {
        sort: false,
        filter: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{dataContent[dataIndex].examLevel.join(', ')}</Typography>
        ),
      },
    },
    {
      name: 'feePayable',
      options: {
        filter: false,
        sort: false,
        searchable: false,
        setCellProps: () => ({ style: { width: '15%', minWidth: '200px' } }),
        customHeadLabelRender: () => (
          <Typography variant="h6" style={{ textAlign: 'right' }}>
            {t('exam->>ReviewExam.Fees Payable')}
            <br />
            {t('exam->>ReviewExam.includingGST')}
          </Typography>
        ),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
          const openViewFeeModal = () => {
            setFeeModalOpen({
              examRegistrationUuid: dataContent[dataIndex].examRegistrationUuid,
              candidateName: dataContent[dataIndex].candidateName,
            });
          };
          return (
            <Box className={classes.actionButtonSmall}>
              <Typography align="right" id={`text-feesPayable-row${rowIndex}`} variant="body2">
                {`$${dataContent[dataIndex].feePayable}`}
              </Typography>
              {componentUsageFor === ComponentUsage.REVIEW && (
                <Button
                  className={classes.noPaddingButton}
                  id={`button-view-fees-details-${dataIndex}`}
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => openViewFeeModal()}
                >
                  {t('exam->>ReviewExam.ViewFeesDetails')}
                </Button>
              )}
            </Box>
          );
        },
      },
    },
    ...candidateDataColumnsForSummary({
      candidateTabData,
      t,
      classes,
      theme,
      componentUsageFor,
      dataTableHandler,
      apiHooks,
      applicationUuid,
      totalFee,
    }),
  ];
};

interface ICandidateListTab extends ClassesAndTranslation {
  candidateDataTableHandler: {
    candidateTabTableState: TableState;
    candidateTabTableDispatch: React.Dispatch<TableActionTypes>;
  };
  applicationUuid: string;
  componentUsageFor: ComponentUsage;
  totalFee: string;
}

const initialModalState = { examRegistrationUuid: '', candidateName: '' };
const CandidateListTab = ({
  candidateDataTableHandler,
  t,
  classes,
  componentUsageFor,
  applicationUuid,
  totalFee,
}: ICandidateListTab) => {
  const [feeModalOpen, setFeeModalOpen] = useState(initialModalState);
  const { candidateTabTableState, candidateTabTableDispatch } = candidateDataTableHandler;
  const isInitialMount = useRef(true);
  const history = useHistory();
  const theme = useTheme();
  const getPaymentStatus = useGetPaymentStatus();
  const paymentStatusDropdown = useAPIDropdown(
    () => getPaymentStatus,
    true,
    dropDownMappingToSelect
  );

  const { snackbarHandler } = useSnackbarHandler();

  const { payload, tableMutation } = useTabMutation({
    componentUsageFor,
    applicationUuid,
    dataTableHandler: {
      tabTableState: candidateTabTableState,
      tabTableDispatch: candidateTabTableDispatch,
    },
    registrationRequestTab: RegistrationRequestTab.CANDIDATE_LIST,
  });

  const postIndividualPaymentLink = usePostGroupExamRegistrationIndividualPaymentLink({
    onSuccess: (res, variables) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>RegistrationSummary.PaymentLinkSentSuccess', {
          candidateName: variables.callbackVariable.candidateName,
        }),
      });
    },
    onError: (error, variables) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>RegistrationSummary.PaymentLinkSentError', {
          candidateName: variables.callbackVariable.candidateName,
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
    onSettled: () => {
      postIndividualPaymentLink.reset();
    },
  });
  useEffect(() => {
    /** only run this useeffect on update, not on mount;
     * to prevent calling the same api twice on mount
     */
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // @ts-ignore
      tableMutation.mutate(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidateTabTableState.page,
    candidateTabTableState.order,
    candidateTabTableState.size,
    candidateTabTableState.filters,
  ]);

  const closeFeeDetailsModalHandler = () => {
    setFeeModalOpen(initialModalState);
  };
  return (
    <>
      <ViewFeeDetailsDialog feeModalOpen={feeModalOpen} onClose={closeFeeDetailsModalHandler} />
      <MUIDataTable
        columns={candidateDataColumns({
          candidateTabData: {
            paymentStatusDropdown,
            dataContent: candidateTabTableState.data,
          },
          t,
          dataTableHandler: candidateDataTableHandler,
          apiHooks: {
            postIndividualPaymentLink,
          },
          classes,
          theme,
          setFeeModalOpen,
          componentUsageFor,
          history,
          applicationUuid,
          totalFee,
        })}
        data={candidateTabTableState.data}
        state={candidateTabTableState}
        dispatch={candidateTabTableDispatch}
        options={{
          pagination: true,
          filter: componentUsageFor === ComponentUsage.SUMMARY,
          filterType: 'multiselect',
          selectToolbarPlacement: componentUsageFor === ComponentUsage.SUMMARY ? 'none' : 'above',
        }}
        rowsPerPageOptions={[5, 10, 20, 30, 50]}
      />
    </>
  );
};

export default CandidateListTab;
